import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { useSiteInfo } from "../context/SiteInfoContext";
import { useHomeContent } from "../context/HomeContentContext";
import api, { formatGBP, formatApiError } from "../lib/api";
import { Trash2, Edit2, Plus, Truck, Package, Upload, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const blankProduct = {
  name: "", description: "", price: 0, category: "rings", subcategory: "",
  audience: ["women"],
  images: [""], stock: 10, sku: "", tags: [], material: "",
  collection_name: "",
  is_featured: false, is_active: true,
};

const TABS = [
  { k: "dashboard", l: "Dashboard" },
  { k: "products", l: "Products" },
  { k: "categories", l: "Categories" },
  { k: "orders", l: "Orders" },
  { k: "returns", l: "Returns" },
  { k: "promos", l: "Promos" },
  { k: "blog", l: "Journal" },
  { k: "pages", l: "Pages" },
  { k: "amazon", l: "Amazon" },
  { k: "emails", l: "Emails" },
  { k: "marketing", l: "Marketing" },
  { k: "livechat", l: "Live Chat" },
  { k: "siteinfo", l: "Site Info" },
  { k: "home", l: "Home" },
  { k: "theme", l: "Theme" },
];

const blankPost = {
  title: "", slug: "", excerpt: "", cover_image: "", body: "",
  tags: [], is_published: true, seo_title: "", seo_description: "",
};

const blankPage = {
  title: "", slug: "", excerpt: "", body: "",
  is_published: true, order: 0, seo_title: "", seo_description: "",
};

export default function Admin() {
  const { user, loading } = useAuth();
  const { theme, update: updateTheme } = useTheme();
  const { info: siteInfo, update: updateSiteInfo } = useSiteInfo();
  const { content: homeContent, update: updateHomeContent } = useHomeContent();
  const [tab, setTab] = useState("dashboard");
  const [analytics, setAnalytics] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [returns, setReturns] = useState([]);
  const [promos, setPromos] = useState([]);
  const [emailLog, setEmailLog] = useState([]);
  const [amazonLog, setAmazonLog] = useState([]);
  const [editing, setEditing] = useState(null);
  const [editPost, setEditPost] = useState(null);
  const [posts, setPosts] = useState([]);
  const [chatCfg, setChatCfg] = useState({ provider: "tawk", property_id: "", widget_id: "", enabled: false });
  const [siteDraft, setSiteDraft] = useState(null);
  const [categories, setCategories] = useState([]);
  const [newCat, setNewCat] = useState({ slug: "", name: "", parent_slug: "", order: 0, collection_name: "" });
  const [newPromo, setNewPromo] = useState({ code: "", type: "percent", value: 10, min_subtotal: 0, is_active: true });
  const [themeDraft, setThemeDraft] = useState(null);
  const [pageList, setPageList] = useState([]);
  const [editPage, setEditPage] = useState(null);
  const [homeDraft, setHomeDraft] = useState(null);

  const refresh = () => {
    api.get("/admin/analytics").then(({ data }) => setAnalytics(data)).catch(() => {});
    api.get("/products?limit=200").then(({ data }) => setProducts(data.items));
    api.get("/admin/orders").then(({ data }) => setOrders(data)).catch(() => {});
    api.get("/admin/returns").then(({ data }) => setReturns(data)).catch(() => {});
    api.get("/admin/promos").then(({ data }) => setPromos(data)).catch(() => {});
    api.get("/admin/email-log").then(({ data }) => setEmailLog(data)).catch(() => {});
    api.get("/admin/amazon/log").then(({ data }) => setAmazonLog(data)).catch(() => {});
    api.get("/admin/blog").then(({ data }) => setPosts(data)).catch(() => {});
    api.get("/livechat").then(({ data }) => setChatCfg({ provider: "tawk", property_id: "", widget_id: "", enabled: false, ...data })).catch(() => {});
    api.get("/categories").then(({ data }) => setCategories(data)).catch(() => {});
    api.get("/admin/pages").then(({ data }) => setPageList(data)).catch(() => {});
  };

  useEffect(() => { if (user?.role === "admin") refresh(); }, [user]);
  useEffect(() => { if (theme && !themeDraft) setThemeDraft(theme); }, [theme, themeDraft]);
  useEffect(() => { if (siteInfo && !siteDraft) setSiteDraft({ ...siteInfo, address_lines: (siteInfo.address_lines || []).join("\n") }); }, [siteInfo, siteDraft]);
  useEffect(() => {
    if (homeContent && !homeDraft) {
      setHomeDraft({
        ...homeContent,
        marquee_items: (homeContent.marquee_items || []).join("\n"),
        category_tiles: homeContent.category_tiles || [],
      });
    }
  }, [homeContent, homeDraft]);

  if (loading) return <main className="py-32 text-center">Loading…</main>;
  if (!user || user.role !== "admin") return <Navigate to="/login" />;

  const saveProduct = async (e) => {
    e.preventDefault();
    const payload = {
      ...editing,
      price: parseFloat(editing.price),
      stock: parseInt(editing.stock || 0),
      images: (typeof editing.images === "string" ? editing.images.split(",").map(s => s.trim()).filter(Boolean) : editing.images),
      tags: (typeof editing.tags === "string" ? editing.tags.split(",").map(s => s.trim()).filter(Boolean) : editing.tags),
    };
    try {
      if (editing.id) await api.patch(`/products/${editing.id}`, payload);
      else await api.post("/products", payload);
      toast.success("Saved"); setEditing(null); refresh();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try { await api.delete(`/products/${id}`); toast.success("Deleted"); refresh(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const updateOrder = async (id, status) => {
    try { await api.patch(`/admin/orders/${id}`, { status }); toast.success("Order updated"); refresh(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const saveOrderShipping = async (id, patch) => {
    try { await api.patch(`/admin/orders/${id}`, patch); toast.success("Shipping details saved"); refresh(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const shipOrder = async (id, payload = {}) => {
    try { await api.post(`/admin/orders/${id}/ship`, payload); toast.success("Marked as shipped — customer notified"); refresh(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const decideReturn = async (rid, decision) => {
    const note = window.prompt(`${decision === "approved" ? "Approval" : "Rejection"} note (optional)`) || "";
    try { await api.patch(`/admin/returns/${rid}`, { decision, note }); toast.success(`Return ${decision}`); refresh(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const createPromo = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/promos", {
        ...newPromo, value: parseFloat(newPromo.value),
        min_subtotal: parseFloat(newPromo.min_subtotal || 0)
      });
      toast.success("Promo created"); setNewPromo({ code: "", type: "percent", value: 10, min_subtotal: 0, is_active: true });
      refresh();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const deletePromo = async (id) => {
    try { await api.delete(`/admin/promos/${id}`); refresh(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const syncAmazon = async (pid) => {
    try {
      const fn = pid ? api.post(`/admin/amazon/sync/${pid}`) : api.post(`/admin/amazon/sync-all`);
      const { data } = await fn;
      toast.success(`Synced (mocked) — ${data.count || 1} listing(s)`); refresh();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const uploadCsv = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/admin/products/bulk", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.created} products imported${data.errors.length ? ` · ${data.errors.length} errors` : ""}`);
      if (data.errors.length) console.warn(data.errors);
      refresh();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const saveTheme = async () => {
    try { await updateTheme(themeDraft); toast.success("Theme updated"); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const savePost = async (e) => {
    e.preventDefault();
    const payload = {
      ...editPost,
      tags: typeof editPost.tags === "string"
        ? editPost.tags.split(",").map((s) => s.trim()).filter(Boolean)
        : editPost.tags,
    };
    try {
      if (editPost.id) await api.patch(`/admin/blog/${editPost.id}`, payload);
      else await api.post("/admin/blog", payload);
      toast.success("Post saved"); setEditPost(null); refresh();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const deletePost = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    try { await api.delete(`/admin/blog/${id}`); refresh(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const saveChat = async () => {
    try { await api.patch("/admin/livechat", chatCfg); toast.success("Live chat settings saved"); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const saveSite = async () => {
    try {
      const payload = {
        ...siteDraft,
        address_lines: typeof siteDraft.address_lines === "string"
          ? siteDraft.address_lines.split("\n").map((s) => s.trim()).filter(Boolean)
          : siteDraft.address_lines,
      };
      await updateSiteInfo(payload);
      toast.success("Site info saved");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const saveHome = async () => {
    try {
      const payload = {
        ...homeDraft,
        marquee_items: typeof homeDraft.marquee_items === "string"
          ? homeDraft.marquee_items.split("\n").map((s) => s.trim()).filter(Boolean)
          : homeDraft.marquee_items,
        category_tiles: (homeDraft.category_tiles || []).filter(t => t && (t.slug || t.label)),
      };
      await updateHomeContent(payload);
      toast.success("Home content saved");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const updateHomeTile = (idx, patch) => {
    const tiles = [...(homeDraft.category_tiles || [])];
    tiles[idx] = { ...tiles[idx], ...patch };
    setHomeDraft({ ...homeDraft, category_tiles: tiles });
  };

  const addHomeTile = () => {
    setHomeDraft({ ...homeDraft, category_tiles: [...(homeDraft.category_tiles || []), { slug: "", label: "", image: "" }] });
  };

  const removeHomeTile = (idx) => {
    const tiles = [...(homeDraft.category_tiles || [])];
    tiles.splice(idx, 1);
    setHomeDraft({ ...homeDraft, category_tiles: tiles });
  };

  const createCat = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/categories", { ...newCat, order: parseInt(newCat.order || 0), parent_slug: newCat.parent_slug || null });
      setNewCat({ slug: "", name: "", parent_slug: "", order: 0, collection_name: "" });
      toast.success("Category created"); refresh();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const deleteCat = async (slug) => {
    if (!window.confirm(`Delete "${slug}"? This will also remove its subcategories.`)) return;
    try { await api.delete(`/admin/categories/${slug}`); refresh(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const updateCatField = async (slug, payload) => {
    try { await api.patch(`/admin/categories/${slug}`, payload); refresh(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const savePage = async (e) => {
    e.preventDefault();
    try {
      if (editPage.id) await api.patch(`/admin/pages/${editPage.id}`, editPage);
      else await api.post("/admin/pages", editPage);
      toast.success("Page saved"); setEditPage(null); refresh();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const deletePage = async (id) => {
    if (!window.confirm("Delete this page?")) return;
    try { await api.delete(`/admin/pages/${id}`); refresh(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <main className="max-w-[1480px] mx-auto px-6 md:px-12 py-14" data-testid="admin-page">
      <div className="flex justify-between items-end mb-10">
        <div>
          <p className="eyebrow text-neutral-600">{siteInfo?.brand_name || "Glitterz SHC"}</p>
          <h1 className="font-serif text-4xl md:text-5xl mt-2">Atelier Console</h1>
        </div>
      </div>

      <div className="flex flex-wrap gap-x-8 gap-y-2 border-b border-hair mb-10">
        {TABS.map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={`admin-tab-${t.k}`}
            className={`pb-4 text-sm tracking-[0.18em] uppercase ${tab === t.k ? "border-b-2 border-[var(--gold)] text-[var(--gold)]" : "text-neutral-600"}`}>{t.l}</button>
        ))}
      </div>

      {tab === "dashboard" && analytics && (
        <section data-testid="admin-dashboard">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
            {[
              { l: "Revenue (paid)", v: formatGBP(analytics.revenue) },
              { l: "Orders Paid", v: analytics.orders_paid },
              { l: "Pending", v: analytics.orders_pending },
              { l: "Customers", v: analytics.customers },
              { l: "Returns Pending", v: analytics.returns_pending },
            ].map((s) => (
              <div key={s.l} className="border border-hair p-6">
                <p className="eyebrow text-neutral-600">{s.l}</p>
                <p className="font-serif text-3xl mt-3">{s.v}</p>
              </div>
            ))}
          </div>
          <div className="grid lg:grid-cols-2 gap-10 mt-12">
            <div>
              <h3 className="font-serif text-2xl mb-4">Top Products</h3>
              <div className="border border-hair">
                {analytics.top_products.map((p) => (
                  <div key={p.id} className="flex justify-between p-4 border-b border-hair last:border-b-0 text-sm">
                    <span>{p.name}</span><span className="text-neutral-500">{formatGBP(p.price)} · pop {p.popularity}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h3 className="font-serif text-2xl mb-4">Low Stock</h3>
              <div className="border border-hair">
                {analytics.low_stock.length === 0 ? <p className="p-4 text-sm text-neutral-500">Healthy stock levels.</p> :
                  analytics.low_stock.map((p) => (
                    <div key={p.id} className="flex justify-between p-4 border-b border-hair last:border-b-0 text-sm">
                      <span>{p.name}</span><span className="text-[var(--gold)]">{p.stock} left</span>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {tab === "products" && (
        <section data-testid="admin-products">
          <div className="flex flex-wrap gap-3 justify-end mb-6">
            <label className="btn-ghost cursor-pointer" data-testid="upload-csv-btn">
              <Upload className="w-4 h-4 inline mr-2" strokeWidth={1.25} /> Bulk CSV import
              <input type="file" accept=".csv" onChange={uploadCsv} className="hidden" />
            </label>
            <button onClick={() => setEditing({ ...blankProduct })} className="btn-primary" data-testid="add-product-btn">
              <Plus className="w-4 h-4" strokeWidth={1.5} /> New Product
            </button>
          </div>
          <p className="text-xs text-neutral-500 mb-4">CSV columns: <code>name,description,price,category,sku,stock,images (pipe-separated),tags (pipe-separated),material,featured</code></p>
          <div className="border border-hair">
            <div className="grid grid-cols-[80px_2fr_1fr_1fr_1fr_120px] gap-4 p-4 border-b border-hair text-xs uppercase tracking-[0.18em] text-neutral-600 bg-cream">
              <span></span><span>Name</span><span>Category</span><span>Price</span><span>Stock</span><span></span>
            </div>
            {products.map((p) => (
              <div key={p.id} className="grid grid-cols-[80px_2fr_1fr_1fr_1fr_120px] gap-4 p-4 border-b border-hair items-center text-sm" data-testid={`admin-row-${p.id}`}>
                <div className="w-14 h-16 bg-sand"><img src={p.images?.[0]} alt="" className="w-full h-full object-cover" /></div>
                <span className="font-serif">{p.name}</span>
                <span className="capitalize text-neutral-500">{p.category}</span>
                <span>{formatGBP(p.price)}</span>
                <span className={p.stock < 5 ? "text-[var(--gold)]" : "text-neutral-700"}>{p.stock}</span>
                <div className="flex gap-2 justify-end">
                  <button onClick={() => setEditing({ ...p, images: p.images.join(", "), tags: (p.tags||[]).join(", ") })} className="p-2 hover:text-[var(--gold)]"><Edit2 className="w-4 h-4" strokeWidth={1.25} /></button>
                  <button onClick={() => remove(p.id)} className="p-2 hover:text-[var(--gold)]"><Trash2 className="w-4 h-4" strokeWidth={1.25} /></button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {tab === "orders" && (
        <section data-testid="admin-orders">
          <div className="border border-hair">
            <div className="grid grid-cols-[1fr_1.4fr_1fr_1fr_1fr_60px] gap-3 p-4 border-b border-hair text-xs uppercase tracking-[0.18em] text-neutral-600 bg-cream">
              <span>ID</span><span>Customer</span><span>Total</span><span>Payment</span><span>Status</span><span></span>
            </div>
            {orders.map((o) => (
              <OrderRow
                key={o.id}
                order={o}
                onStatus={(s) => updateOrder(o.id, s)}
                onSave={(p) => saveOrderShipping(o.id, p)}
                onShip={(p) => shipOrder(o.id, p)}
              />
            ))}
            {orders.length === 0 && <p className="p-6 text-sm text-neutral-500">No orders yet.</p>}
          </div>
        </section>
      )}

      {tab === "returns" && (
        <section data-testid="admin-returns">
          {returns.length === 0 ? <p className="text-sm text-neutral-500">No returns yet.</p> :
            returns.map((r) => (
              <div key={r.id} className="border border-hair p-6 mb-4" data-testid={`admin-return-${r.id}`}>
                <div className="flex justify-between mb-2 text-sm">
                  <span className="font-medium">#{r.id.slice(0,8).toUpperCase()} · {r.customer_email}</span>
                  <span className="eyebrow text-[var(--gold)]">{r.status}</span>
                </div>
                <p className="text-xs text-neutral-500">For order {r.order_id.slice(0,8).toUpperCase()} · {new Date(r.created_at).toLocaleDateString("en-GB")}</p>
                <div className="rule my-3" />
                {r.items.map((it, i) => <p key={i} className="text-sm py-1">{it.name} × {it.quantity} — <span className="text-neutral-500">{it.reason}</span></p>)}
                {r.note && <p className="text-xs italic mt-3 text-neutral-600">"{r.note}"</p>}
                {r.status === "requested" && (
                  <div className="flex gap-3 mt-4">
                    <button onClick={() => decideReturn(r.id, "approved")} className="btn-primary" data-testid={`approve-return-${r.id}`}>Approve</button>
                    <button onClick={() => decideReturn(r.id, "rejected")} className="btn-ghost">Reject</button>
                  </div>
                )}
              </div>
            ))}
        </section>
      )}

      {tab === "promos" && (
        <section data-testid="admin-promos">
          <form onSubmit={createPromo} className="border border-hair p-6 mb-8 grid grid-cols-2 md:grid-cols-5 gap-3">
            <input required placeholder="CODE" value={newPromo.code} onChange={(e) => setNewPromo({ ...newPromo, code: e.target.value.toUpperCase() })} className="border border-hair px-3 py-2 text-sm uppercase" />
            <select value={newPromo.type} onChange={(e) => setNewPromo({ ...newPromo, type: e.target.value })} className="border border-hair px-3 py-2 text-sm">
              <option value="percent">%</option><option value="fixed">£</option>
            </select>
            <input required type="number" step="0.01" placeholder="Value" value={newPromo.value} onChange={(e) => setNewPromo({ ...newPromo, value: e.target.value })} className="border border-hair px-3 py-2 text-sm" />
            <input type="number" step="0.01" placeholder="Min subtotal" value={newPromo.min_subtotal} onChange={(e) => setNewPromo({ ...newPromo, min_subtotal: e.target.value })} className="border border-hair px-3 py-2 text-sm" />
            <button className="btn-primary" data-testid="create-promo-btn"><Plus className="w-4 h-4" strokeWidth={1.5} /> Create</button>
          </form>
          <div className="border border-hair">
            <div className="grid grid-cols-[1fr_1fr_1fr_1fr_1fr_80px] gap-3 p-4 border-b border-hair text-xs uppercase tracking-[0.18em] text-neutral-600 bg-cream">
              <span>Code</span><span>Type</span><span>Value</span><span>Uses</span><span>Active</span><span></span>
            </div>
            {promos.map((p) => (
              <div key={p.id} className="grid grid-cols-[1fr_1fr_1fr_1fr_1fr_80px] gap-3 p-4 border-b border-hair text-sm items-center">
                <span className="font-mono">{p.code}{p.user_id ? <span className="ml-1 text-[var(--gold)] text-xs">(personal)</span> : ""}</span>
                <span>{p.type}</span>
                <span>{p.type === "percent" ? `${p.value}%` : formatGBP(p.value)}</span>
                <span>{p.uses}{p.max_uses ? ` / ${p.max_uses}` : ""}</span>
                <span>{p.is_active ? "✓" : "—"}</span>
                <button onClick={() => deletePromo(p.id)} className="text-neutral-500 hover:text-[var(--gold)]"><Trash2 className="w-4 h-4" strokeWidth={1.25} /></button>
              </div>
            ))}
          </div>
        </section>
      )}

      {tab === "pages" && (
        <section data-testid="admin-pages">
          <div className="flex justify-end mb-6">
            <button onClick={() => setEditPage({ ...blankPage })} className="btn-primary" data-testid="add-page-btn">
              <Plus className="w-4 h-4" strokeWidth={1.5} /> New Page
            </button>
          </div>
          <p className="text-xs text-neutral-500 mb-4">Custom pages appear in the footer's Care column at <code>/pages/&lt;slug&gt;</code>. Use markdown for the body.</p>
          <div className="border border-hair">
            <div className="grid grid-cols-[2fr_1fr_60px_1fr_120px] gap-4 p-4 border-b border-hair text-xs uppercase tracking-[0.18em] text-neutral-600 bg-cream">
              <span>Title</span><span>Slug</span><span>Order</span><span>Status</span><span></span>
            </div>
            {pageList.map((p) => (
              <div key={p.id} className="grid grid-cols-[2fr_1fr_60px_1fr_120px] gap-4 p-4 border-b border-hair items-center text-sm" data-testid={`page-row-${p.slug}`}>
                <span className="font-serif">{p.title}</span>
                <span className="font-mono text-xs text-neutral-500">/pages/{p.slug}</span>
                <span className="text-xs">{p.order}</span>
                <span className="text-xs">{p.is_published ? "Published" : "Draft"}</span>
                <div className="flex gap-2 justify-end">
                  <a href={`/pages/${p.slug}`} target="_blank" rel="noreferrer" className="p-2 hover:text-[var(--gold)] text-xs uppercase tracking-[0.18em]">View</a>
                  <button onClick={() => setEditPage({ ...p })} className="p-2 hover:text-[var(--gold)]"><Edit2 className="w-4 h-4" strokeWidth={1.25} /></button>
                  <button onClick={() => deletePage(p.id)} className="p-2 hover:text-[var(--gold)]"><Trash2 className="w-4 h-4" strokeWidth={1.25} /></button>
                </div>
              </div>
            ))}
            {pageList.length === 0 && <p className="p-6 text-sm text-neutral-500">No pages yet.</p>}
          </div>
        </section>
      )}


      {tab === "amazon" && (
        <section data-testid="admin-amazon">
          <div className="bg-sand border border-hair p-6 mb-6">
            <p className="eyebrow text-neutral-600 mb-2">Stub mode (mocked)</p>
            <p className="text-sm text-neutral-700 max-w-2xl">Listings sync uses an Amazon SP-API stub for now. Each "sync" stamps the product with a mock ASIN and logs the action. Wire your Seller Central LWA + IAM credentials in <code>backend/.env</code> later to make this live.</p>
            <button onClick={() => syncAmazon(null)} className="btn-primary mt-4" data-testid="sync-all-amazon">
              <RefreshCw className="w-4 h-4" strokeWidth={1.5} /> Sync All Listings
            </button>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            <div>
              <h3 className="font-serif text-2xl mb-4">Products</h3>
              <div className="border border-hair">
                {products.map((p) => (
                  <div key={p.id} className="grid grid-cols-[2fr_1fr_120px] gap-3 p-3 border-b border-hair last:border-b-0 text-sm items-center">
                    <span className="font-serif">{p.name}</span>
                    <span className={p.amazon_synced ? "text-[var(--gold)] text-xs" : "text-neutral-400 text-xs"}>
                      {p.amazon_synced ? `ASIN ${p.amazon_asin || "•"}` : "Not synced"}
                    </span>
                    <button onClick={() => syncAmazon(p.id)} className="btn-ghost text-xs">Sync</button>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h3 className="font-serif text-2xl mb-4">Sync Log</h3>
              <div className="border border-hair">
                {amazonLog.length === 0 ? <p className="p-4 text-sm text-neutral-500">No sync activity yet.</p> :
                  amazonLog.map((l, i) => (
                    <div key={i} className="p-3 border-b border-hair last:border-b-0 text-sm">
                      <p>{l.action} {l.count ? `(${l.count} items)` : ""}</p>
                      <p className="text-xs text-neutral-500">{new Date(l.at).toLocaleString("en-GB")} — {l.status}</p>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {tab === "emails" && (
        <section data-testid="admin-emails">
          <p className="text-sm text-neutral-500 mb-6">Recent transactional + abandoned-cart emails. Add <code>SENDGRID_API_KEY</code> to backend env to send for real.</p>
          <div className="border border-hair">
            <div className="grid grid-cols-[1fr_2fr_1fr_1fr] gap-3 p-4 border-b border-hair text-xs uppercase tracking-[0.18em] text-neutral-600 bg-cream">
              <span>To</span><span>Subject</span><span>Kind</span><span>Status</span>
            </div>
            {emailLog.map((e) => (
              <div key={e.id} className="grid grid-cols-[1fr_2fr_1fr_1fr] gap-3 p-4 border-b border-hair text-sm">
                <span className="truncate">{e.to}</span>
                <span className="truncate">{e.subject}</span>
                <span className="text-xs uppercase tracking-[0.18em] text-neutral-500">{e.kind}</span>
                <span className={e.sent ? "text-[var(--gold)]" : "text-neutral-500"}>{e.sent ? "Sent" : "Mocked / queued"}</span>
              </div>
            ))}
            {emailLog.length === 0 && <p className="p-6 text-sm text-neutral-500">No emails yet.</p>}
          </div>
        </section>
      )}

      {tab === "blog" && (
        <section data-testid="admin-blog">
          <div className="flex justify-end mb-6">
            <button onClick={() => setEditPost({ ...blankPost })} className="btn-primary" data-testid="add-post-btn">
              <Plus className="w-4 h-4" strokeWidth={1.5} /> New Post
            </button>
          </div>
          <div className="border border-hair">
            <div className="grid grid-cols-[80px_2fr_1fr_1fr_120px] gap-4 p-4 border-b border-hair text-xs uppercase tracking-[0.18em] text-neutral-600 bg-cream">
              <span></span><span>Title</span><span>Slug</span><span>Status</span><span></span>
            </div>
            {posts.map((p) => (
              <div key={p.id} className="grid grid-cols-[80px_2fr_1fr_1fr_120px] gap-4 p-4 border-b border-hair items-center text-sm" data-testid={`post-row-${p.slug}`}>
                <div className="w-14 h-14 bg-sand">{p.cover_image && <img src={p.cover_image} alt="" className="w-full h-full object-cover" />}</div>
                <span className="font-serif">{p.title}</span>
                <span className="font-mono text-xs text-neutral-500">{p.slug}</span>
                <span className="text-xs">{p.is_published ? "Published" : "Draft"}</span>
                <div className="flex gap-2 justify-end">
                  <button onClick={() => setEditPost({ ...p, tags: (p.tags || []).join(", ") })} className="p-2 hover:text-[var(--gold)]"><Edit2 className="w-4 h-4" strokeWidth={1.25} /></button>
                  <button onClick={() => deletePost(p.id)} className="p-2 hover:text-[var(--gold)]"><Trash2 className="w-4 h-4" strokeWidth={1.25} /></button>
                </div>
              </div>
            ))}
            {posts.length === 0 && <p className="p-6 text-sm text-neutral-500">No posts yet.</p>}
          </div>
        </section>
      )}

      {tab === "marketing" && <MarketingTab />}


      {tab === "livechat" && (
        <section data-testid="admin-livechat" className="max-w-2xl space-y-4">
          <div className="bg-sand border border-hair p-6">
            <p className="eyebrow text-neutral-600 mb-2">Provider</p>
            <p className="text-sm text-neutral-700">Embed a third-party live-chat widget (Tawk.to or Crisp). Without a provider configured, the site shows a graceful "Concierge" mailto button at the bottom-left.</p>
          </div>
          <div>
            <p className="eyebrow text-neutral-600 mb-2">Provider</p>
            <select value={chatCfg.provider} onChange={(e) => setChatCfg({ ...chatCfg, provider: e.target.value })} className="border border-hair px-3 py-2.5 text-sm w-full">
              <option value="tawk">Tawk.to</option>
              <option value="crisp">Crisp</option>
            </select>
          </div>
          <div>
            <p className="eyebrow text-neutral-600 mb-2">Property ID {chatCfg.provider === "tawk" ? "(Tawk Property ID)" : "(Crisp Website ID)"}</p>
            <input value={chatCfg.property_id || ""} onChange={(e) => setChatCfg({ ...chatCfg, property_id: e.target.value })}
              data-testid="chat-property-id" className="border border-hair px-3 py-2.5 text-sm w-full font-mono" placeholder="e.g. 65f2c…" />
          </div>
          {chatCfg.provider === "tawk" && (
            <div>
              <p className="eyebrow text-neutral-600 mb-2">Widget ID (Tawk only)</p>
              <input value={chatCfg.widget_id || ""} onChange={(e) => setChatCfg({ ...chatCfg, widget_id: e.target.value })}
                data-testid="chat-widget-id" className="border border-hair px-3 py-2.5 text-sm w-full font-mono" placeholder="default" />
            </div>
          )}
          <label className="flex items-center gap-3 text-sm">
            <input type="checkbox" checked={!!chatCfg.enabled} onChange={(e) => setChatCfg({ ...chatCfg, enabled: e.target.checked })} data-testid="chat-enabled" />
            Enabled (load widget on every page)
          </label>
          <button onClick={saveChat} className="btn-primary" data-testid="save-chat-btn">Save settings</button>
        </section>
      )}

      {tab === "categories" && (
        <section data-testid="admin-categories" className="grid lg:grid-cols-2 gap-10">
          <div>
            <h3 className="font-serif text-2xl mb-4">Categories</h3>
            <p className="text-xs text-neutral-500 mb-4">Top-level categories appear in the navbar. Subcategories appear as filter chips on the shop page.</p>
            <div className="border border-hair">
              {categories.filter((c) => !c.parent_slug).map((top) => (
                <div key={top.slug} className="border-b border-hair last:border-b-0">
                  <div className="grid grid-cols-[1fr_120px_60px_80px] gap-3 p-4 bg-cream items-center text-sm" data-testid={`cat-${top.slug}`}>
                    <span className="font-serif">{top.name} <span className="text-xs text-neutral-500 font-mono ml-2">/{top.slug}</span></span>
                    <input type="number" defaultValue={top.order} onBlur={(e) => updateCatField(top.slug, { order: parseInt(e.target.value || 0) })} className="border border-hair px-2 py-1 text-xs w-16" />
                    <span className="text-xs text-neutral-500">{categories.filter((c) => c.parent_slug === top.slug).length} subs</span>
                    <button onClick={() => deleteCat(top.slug)} className="text-neutral-500 hover:text-[var(--gold)]"><Trash2 className="w-4 h-4" strokeWidth={1.25} /></button>
                  </div>
                  {categories.filter((c) => c.parent_slug === top.slug).map((sub) => (
                    <div key={sub.slug} className="grid grid-cols-[1fr_120px_60px_80px] gap-3 p-3 pl-10 items-center text-sm border-t border-hair" data-testid={`subcat-${sub.slug}`}>
                      <span>{sub.name} <span className="text-xs text-neutral-500 font-mono ml-2">/{sub.slug}</span></span>
                      <input type="number" defaultValue={sub.order} onBlur={(e) => updateCatField(sub.slug, { order: parseInt(e.target.value || 0) })} className="border border-hair px-2 py-1 text-xs w-16" />
                      <span></span>
                      <button onClick={() => deleteCat(sub.slug)} className="text-neutral-500 hover:text-[var(--gold)]"><Trash2 className="w-3.5 h-3.5" strokeWidth={1.25} /></button>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
          <div>
            <h3 className="font-serif text-2xl mb-4">Add new</h3>
            <form onSubmit={createCat} className="space-y-3 border border-hair p-6">
              <div>
                <p className="eyebrow text-neutral-600 mb-2">Name</p>
                <input required value={newCat.name} onChange={(e) => setNewCat({ ...newCat, name: e.target.value, slug: newCat.slug || e.target.value.toLowerCase().replace(/\s+/g, "-") })} data-testid="newcat-name"
                  className="border border-hair px-3 py-2.5 text-sm w-full" placeholder="e.g. Anklets" />
              </div>
              <div>
                <p className="eyebrow text-neutral-600 mb-2">Slug</p>
                <input required value={newCat.slug} onChange={(e) => setNewCat({ ...newCat, slug: e.target.value })} data-testid="newcat-slug"
                  className="border border-hair px-3 py-2.5 text-sm w-full font-mono" placeholder="anklets" />
              </div>
              <div>
                <p className="eyebrow text-neutral-600 mb-2">Parent (leave blank for top-level)</p>
                <select value={newCat.parent_slug} onChange={(e) => setNewCat({ ...newCat, parent_slug: e.target.value })} data-testid="newcat-parent"
                  className="border border-hair px-3 py-2.5 text-sm w-full">
                  <option value="">— Top level —</option>
                  {categories.filter((c) => !c.parent_slug).map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <p className="eyebrow text-neutral-600 mb-2">Display order</p>
                <input type="number" value={newCat.order} onChange={(e) => setNewCat({ ...newCat, order: e.target.value })} className="border border-hair px-3 py-2.5 text-sm w-full" />
              </div>
              <div>
                <p className="eyebrow text-neutral-600 mb-2">Collection name (optional)</p>
                <input value={newCat.collection_name || ""} onChange={(e) => setNewCat({ ...newCat, collection_name: e.target.value })} data-testid="newcat-collection" placeholder="e.g. Heritage" className="border border-hair px-3 py-2.5 text-sm w-full" />
              </div>
              <button className="btn-primary" data-testid="create-cat-btn"><Plus className="w-4 h-4" strokeWidth={1.5} /> Create category</button>
            </form>
          </div>
        </section>
      )}


      {tab === "siteinfo" && siteDraft && (
        <section data-testid="admin-siteinfo" className="grid md:grid-cols-2 gap-10 max-w-5xl">
          <div className="space-y-4">
            <div>
              <p className="eyebrow text-neutral-600 mb-2">Brand name</p>
              <input value={siteDraft.brand_name || ""} onChange={(e) => setSiteDraft({ ...siteDraft, brand_name: e.target.value })}
                placeholder="Glitterz·SHC" data-testid="site-brand-name"
                className="border border-hair px-3 py-2.5 text-sm w-full" />
              <p className="text-xs text-neutral-500 mt-1">Use a centred dot (·) to split the gold accent — e.g. "Glitterz·SHC"</p>
            </div>
            <div>
              <p className="eyebrow text-neutral-600 mb-2">Logo image URL (optional)</p>
              <input value={siteDraft.logo_url || ""} onChange={(e) => setSiteDraft({ ...siteDraft, logo_url: e.target.value })}
                placeholder="https://…/logo.png" data-testid="site-logo-url"
                className="border border-hair px-3 py-2.5 text-sm w-full" />
              <p className="text-xs text-neutral-500 mt-1">If provided, replaces the text mark in the navbar. ~40px tall transparent PNG/SVG works best.</p>
            </div>
            <div>
              <p className="eyebrow text-neutral-600 mb-2">Website URL</p>
              <input value={siteDraft.website_url || ""} onChange={(e) => setSiteDraft({ ...siteDraft, website_url: e.target.value })}
                placeholder="https://glitterzshc.com" data-testid="site-website-url"
                className="border border-hair px-3 py-2.5 text-sm w-full" />
              <p className="text-xs text-neutral-500 mt-1">Used in SEO schema, sitemap, transactional email links.</p>
            </div>
            <div>
              <p className="eyebrow text-neutral-600 mb-2">Announcement bar</p>
              <input value={siteDraft.announcement || ""} onChange={(e) => setSiteDraft({ ...siteDraft, announcement: e.target.value })}
                data-testid="site-announcement" className="border border-hair px-3 py-2.5 text-sm w-full" />
            </div>
            <div>
              <p className="eyebrow text-neutral-600 mb-2">Address (one line per row)</p>
              <textarea rows={4} value={siteDraft.address_lines || ""} onChange={(e) => setSiteDraft({ ...siteDraft, address_lines: e.target.value })}
                data-testid="site-address" className="border border-hair px-3 py-2.5 text-sm w-full font-mono" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="eyebrow text-neutral-600 mb-2">Phone</p>
                <input value={siteDraft.phone || ""} onChange={(e) => setSiteDraft({ ...siteDraft, phone: e.target.value })}
                  data-testid="site-phone" className="border border-hair px-3 py-2.5 text-sm w-full" />
              </div>
              <div>
                <p className="eyebrow text-neutral-600 mb-2">Email</p>
                <input type="email" value={siteDraft.email || ""} onChange={(e) => setSiteDraft({ ...siteDraft, email: e.target.value })}
                  data-testid="site-email" className="border border-hair px-3 py-2.5 text-sm w-full" />
              </div>
            </div>
            <div>
              <p className="eyebrow text-neutral-600 mb-2">Opening hours</p>
              <input value={siteDraft.hours || ""} onChange={(e) => setSiteDraft({ ...siteDraft, hours: e.target.value })}
                data-testid="site-hours" className="border border-hair px-3 py-2.5 text-sm w-full" />
            </div>
            <div>
              <p className="eyebrow text-neutral-600 mb-2">VAT number</p>
              <input value={siteDraft.vat_number || ""} onChange={(e) => setSiteDraft({ ...siteDraft, vat_number: e.target.value })}
                data-testid="site-vat" className="border border-hair px-3 py-2.5 text-sm w-full" />
            </div>
            <div>
              <p className="eyebrow text-neutral-600 mb-2">Social links (full URLs, optional)</p>
              <div className="space-y-2">
                {[
                  { k: "instagram", l: "Instagram" },
                  { k: "facebook", l: "Facebook" },
                  { k: "tiktok", l: "TikTok" },
                  { k: "pinterest", l: "Pinterest" },
                  { k: "twitter", l: "X / Twitter" },
                ].map((s) => (
                  <input key={s.k} value={siteDraft[s.k] || ""} onChange={(e) => setSiteDraft({ ...siteDraft, [s.k]: e.target.value })}
                    placeholder={`${s.l} URL`} data-testid={`site-${s.k}`}
                    className="border border-hair px-3 py-2 text-sm w-full" />
                ))}
              </div>
            </div>
            <button onClick={saveSite} className="btn-primary mt-4" data-testid="save-site-btn">Save Site Info</button>
          </div>

          <div className="bg-cream border border-hair p-8 self-start">
            <p className="eyebrow text-neutral-600 mb-3">Live preview</p>
            <div className="bg-[var(--bg-tertiary)] py-3 -mx-8 mb-6 text-center text-[10px] tracking-[0.22em] uppercase text-neutral-700">
              {siteDraft.announcement || "—"}
            </div>
            <div className="font-serif text-2xl tracking-[0.32em] uppercase mb-6 flex items-center gap-2">
              {siteDraft.logo_url ? (
                <img src={siteDraft.logo_url} alt="" className="h-10 w-auto object-contain" />
              ) : (
                <>{(siteDraft.brand_name || "").split("·")[0]}<span className="gold">·</span>{(siteDraft.brand_name || "").split("·")[1]}</>
              )}
            </div>
            <div className="text-sm font-light text-neutral-700 space-y-2">
              {(typeof siteDraft.address_lines === "string" ? siteDraft.address_lines.split("\n") : (siteDraft.address_lines || [])).filter(Boolean).map((l, i) => <p key={i}>{l}</p>)}
              <p className="pt-2">{siteDraft.phone}</p>
              <p>{siteDraft.email}</p>
              <p className="text-xs text-neutral-500">{siteDraft.hours}</p>
            </div>
          </div>
        </section>
      )}

      {tab === "home" && homeDraft && (
        <section data-testid="admin-home" className="space-y-12 max-w-5xl">
          <div>
            <p className="eyebrow text-neutral-600 mb-3">Hero (top of home page)</p>
            <div className="grid md:grid-cols-2 gap-4">
              <label className="block">
                <span className="text-xs uppercase tracking-wide text-neutral-600">Hero image URL</span>
                <input value={homeDraft.hero_image || ""} onChange={(e) => setHomeDraft({ ...homeDraft, hero_image: e.target.value })}
                  data-testid="home-hero-image" className="border border-hair px-3 py-2.5 text-sm w-full mt-1" placeholder="https://…/hero.jpg" />
              </label>
              <label className="block">
                <span className="text-xs uppercase tracking-wide text-neutral-600">Eyebrow (small caps line)</span>
                <input value={homeDraft.hero_eyebrow || ""} onChange={(e) => setHomeDraft({ ...homeDraft, hero_eyebrow: e.target.value })}
                  data-testid="home-hero-eyebrow" className="border border-hair px-3 py-2.5 text-sm w-full mt-1" />
              </label>
              <label className="block md:col-span-2">
                <span className="text-xs uppercase tracking-wide text-neutral-600">Headline (use new lines for stacked words)</span>
                <textarea rows={3} value={homeDraft.hero_headline || ""} onChange={(e) => setHomeDraft({ ...homeDraft, hero_headline: e.target.value })}
                  data-testid="home-hero-headline" className="border border-hair px-3 py-2.5 text-sm w-full mt-1 font-serif" />
              </label>
              <label className="block md:col-span-2">
                <span className="text-xs uppercase tracking-wide text-neutral-600">Sub-tagline</span>
                <input value={homeDraft.hero_subtagline || ""} onChange={(e) => setHomeDraft({ ...homeDraft, hero_subtagline: e.target.value })}
                  data-testid="home-hero-subtagline" className="border border-hair px-3 py-2.5 text-sm w-full mt-1" />
              </label>
              <label className="block">
                <span className="text-xs uppercase tracking-wide text-neutral-600">Primary CTA label</span>
                <input value={homeDraft.hero_cta_label || ""} onChange={(e) => setHomeDraft({ ...homeDraft, hero_cta_label: e.target.value })}
                  data-testid="home-cta-label" className="border border-hair px-3 py-2.5 text-sm w-full mt-1" />
              </label>
              <label className="block">
                <span className="text-xs uppercase tracking-wide text-neutral-600">Primary CTA link</span>
                <input value={homeDraft.hero_cta_link || ""} onChange={(e) => setHomeDraft({ ...homeDraft, hero_cta_link: e.target.value })}
                  data-testid="home-cta-link" className="border border-hair px-3 py-2.5 text-sm w-full mt-1" placeholder="/shop" />
              </label>
              <label className="block">
                <span className="text-xs uppercase tracking-wide text-neutral-600">Secondary CTA label</span>
                <input value={homeDraft.hero_secondary_label || ""} onChange={(e) => setHomeDraft({ ...homeDraft, hero_secondary_label: e.target.value })}
                  data-testid="home-sec-label" className="border border-hair px-3 py-2.5 text-sm w-full mt-1" />
              </label>
              <label className="block">
                <span className="text-xs uppercase tracking-wide text-neutral-600">Secondary CTA link</span>
                <input value={homeDraft.hero_secondary_link || ""} onChange={(e) => setHomeDraft({ ...homeDraft, hero_secondary_link: e.target.value })}
                  data-testid="home-sec-link" className="border border-hair px-3 py-2.5 text-sm w-full mt-1" placeholder="/journal" />
              </label>
            </div>
          </div>

          <div>
            <p className="eyebrow text-neutral-600 mb-3">Marquee strip (one item per line)</p>
            <textarea rows={6} value={typeof homeDraft.marquee_items === "string" ? homeDraft.marquee_items : (homeDraft.marquee_items || []).join("\n")}
              onChange={(e) => setHomeDraft({ ...homeDraft, marquee_items: e.target.value })}
              data-testid="home-marquee" className="border border-hair px-3 py-2.5 text-sm w-full font-mono" />
          </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <p className="eyebrow text-neutral-600">Category tiles (the 4-up grid)</p>
              <button onClick={addHomeTile} className="btn-ghost text-xs" data-testid="home-tile-add">+ Add tile</button>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <label className="block">
                <span className="text-xs uppercase tracking-wide text-neutral-600">Section eyebrow</span>
                <input value={homeDraft.category_section_eyebrow || ""} onChange={(e) => setHomeDraft({ ...homeDraft, category_section_eyebrow: e.target.value })}
                  className="border border-hair px-3 py-2.5 text-sm w-full mt-1" />
              </label>
              <label className="block">
                <span className="text-xs uppercase tracking-wide text-neutral-600">Section title</span>
                <input value={homeDraft.category_section_title || ""} onChange={(e) => setHomeDraft({ ...homeDraft, category_section_title: e.target.value })}
                  className="border border-hair px-3 py-2.5 text-sm w-full mt-1" />
              </label>
            </div>
            <div className="mt-4 space-y-3">
              {(homeDraft.category_tiles || []).map((tile, i) => (
                <div key={i} className="border border-hair p-4 grid md:grid-cols-12 gap-3 items-start" data-testid={`home-tile-${i}`}>
                  <input value={tile.slug || ""} onChange={(e) => updateHomeTile(i, { slug: e.target.value })}
                    placeholder="rings" className="border border-hair px-3 py-2 text-sm md:col-span-2" />
                  <input value={tile.label || ""} onChange={(e) => updateHomeTile(i, { label: e.target.value })}
                    placeholder="Rings" className="border border-hair px-3 py-2 text-sm md:col-span-3" />
                  <input value={tile.image || ""} onChange={(e) => updateHomeTile(i, { image: e.target.value })}
                    placeholder="https://…/image.jpg" className="border border-hair px-3 py-2 text-sm md:col-span-6" />
                  <button onClick={() => removeHomeTile(i)} className="text-xs text-red-700 md:col-span-1" data-testid={`home-tile-remove-${i}`}>Remove</button>
                </div>
              ))}
            </div>
            <p className="text-xs text-neutral-500 mt-2">Slug links to <code>/category/&lt;slug&gt;</code>. Use existing category slugs (rings, necklaces, etc.).</p>
          </div>

          <div>
            <p className="eyebrow text-neutral-600 mb-3">Lifestyle banner (mid-page parallax)</p>
            <div className="grid md:grid-cols-2 gap-4">
              <label className="block md:col-span-2">
                <span className="text-xs uppercase tracking-wide text-neutral-600">Image URL</span>
                <input value={homeDraft.lifestyle_image || ""} onChange={(e) => setHomeDraft({ ...homeDraft, lifestyle_image: e.target.value })}
                  data-testid="home-life-image" className="border border-hair px-3 py-2.5 text-sm w-full mt-1" />
              </label>
              <label className="block">
                <span className="text-xs uppercase tracking-wide text-neutral-600">Eyebrow</span>
                <input value={homeDraft.lifestyle_eyebrow || ""} onChange={(e) => setHomeDraft({ ...homeDraft, lifestyle_eyebrow: e.target.value })}
                  className="border border-hair px-3 py-2.5 text-sm w-full mt-1" />
              </label>
              <label className="block">
                <span className="text-xs uppercase tracking-wide text-neutral-600">Headline</span>
                <input value={homeDraft.lifestyle_headline || ""} onChange={(e) => setHomeDraft({ ...homeDraft, lifestyle_headline: e.target.value })}
                  className="border border-hair px-3 py-2.5 text-sm w-full mt-1" />
              </label>
              <label className="block md:col-span-2">
                <span className="text-xs uppercase tracking-wide text-neutral-600">Body</span>
                <textarea rows={3} value={homeDraft.lifestyle_body || ""} onChange={(e) => setHomeDraft({ ...homeDraft, lifestyle_body: e.target.value })}
                  className="border border-hair px-3 py-2.5 text-sm w-full mt-1" />
              </label>
            </div>
          </div>

          <div>
            <p className="eyebrow text-neutral-600 mb-3">Section headings</p>
            <div className="grid md:grid-cols-2 gap-4">
              <label className="block">
                <span className="text-xs uppercase tracking-wide text-neutral-600">Featured eyebrow</span>
                <input value={homeDraft.featured_eyebrow || ""} onChange={(e) => setHomeDraft({ ...homeDraft, featured_eyebrow: e.target.value })}
                  className="border border-hair px-3 py-2.5 text-sm w-full mt-1" />
              </label>
              <label className="block">
                <span className="text-xs uppercase tracking-wide text-neutral-600">Featured title</span>
                <input value={homeDraft.featured_title || ""} onChange={(e) => setHomeDraft({ ...homeDraft, featured_title: e.target.value })}
                  className="border border-hair px-3 py-2.5 text-sm w-full mt-1" />
              </label>
              <label className="block">
                <span className="text-xs uppercase tracking-wide text-neutral-600">AI Stylist eyebrow</span>
                <input value={homeDraft.ai_eyebrow || ""} onChange={(e) => setHomeDraft({ ...homeDraft, ai_eyebrow: e.target.value })}
                  className="border border-hair px-3 py-2.5 text-sm w-full mt-1" />
              </label>
              <label className="block">
                <span className="text-xs uppercase tracking-wide text-neutral-600">AI Stylist title</span>
                <input value={homeDraft.ai_title || ""} onChange={(e) => setHomeDraft({ ...homeDraft, ai_title: e.target.value })}
                  className="border border-hair px-3 py-2.5 text-sm w-full mt-1" />
              </label>
              <label className="block md:col-span-2">
                <span className="text-xs uppercase tracking-wide text-neutral-600">AI Stylist body (overrides the AI text — leave blank to use Claude-generated copy)</span>
                <textarea rows={4} value={homeDraft.ai_body || ""} onChange={(e) => setHomeDraft({ ...homeDraft, ai_body: e.target.value })}
                  data-testid="home-ai-body" placeholder="Leave blank to use AI-generated copy, or write your own here."
                  className="border border-hair px-3 py-2.5 text-sm w-full mt-1" />
              </label>
              <label className="flex items-center gap-2 md:col-span-2">
                <input type="checkbox" checked={homeDraft.ai_enabled !== false} onChange={(e) => setHomeDraft({ ...homeDraft, ai_enabled: e.target.checked })}
                  data-testid="home-ai-enabled" />
                <span className="text-xs uppercase tracking-wide text-neutral-600">Show AI Stylist section on home</span>
              </label>
            </div>
          </div>

          <div>
            <p className="eyebrow text-neutral-600 mb-3">Announcement bar (top of every page)</p>
            <input value={siteDraft?.announcement || ""}
              onChange={(e) => setSiteDraft({ ...(siteDraft || {}), announcement: e.target.value })}
              data-testid="home-announcement" placeholder="Complimentary UK delivery on orders over £100 · Hand-finished in London"
              className="border border-hair px-3 py-2.5 text-sm w-full" />
            <p className="text-xs text-neutral-500 mt-2">Click <b>Save Announcement</b> below to apply (also editable from the Site Info tab).</p>
            <button onClick={saveSite} className="btn-ghost mt-3 text-xs" data-testid="save-announcement-btn">Save Announcement</button>
          </div>

          <div className="sticky bottom-4">
            <button onClick={saveHome} className="btn-primary" data-testid="save-home-btn">Save Home Content</button>
          </div>
        </section>
      )}


      {tab === "theme" && themeDraft && (
        <section data-testid="admin-theme" className="grid md:grid-cols-2 gap-10 max-w-3xl">
          <div className="space-y-4">
            <div>
              <p className="eyebrow text-neutral-600 mb-2">Primary colour (gold)</p>
              <div className="flex gap-3 items-center">
                <input type="color" value={themeDraft.primary} onChange={(e) => setThemeDraft({ ...themeDraft, primary: e.target.value })} className="w-12 h-10" data-testid="theme-primary" />
                <input value={themeDraft.primary} onChange={(e) => setThemeDraft({ ...themeDraft, primary: e.target.value })} className="border border-hair px-3 py-2 text-sm flex-1" />
              </div>
            </div>
            <div>
              <p className="eyebrow text-neutral-600 mb-2">Primary hover</p>
              <div className="flex gap-3 items-center">
                <input type="color" value={themeDraft.primary_hover} onChange={(e) => setThemeDraft({ ...themeDraft, primary_hover: e.target.value })} className="w-12 h-10" />
                <input value={themeDraft.primary_hover} onChange={(e) => setThemeDraft({ ...themeDraft, primary_hover: e.target.value })} className="border border-hair px-3 py-2 text-sm flex-1" />
              </div>
            </div>
            <div>
              <p className="eyebrow text-neutral-600 mb-2">Cream background</p>
              <div className="flex gap-3 items-center">
                <input type="color" value={themeDraft.bg_soft} onChange={(e) => setThemeDraft({ ...themeDraft, bg_soft: e.target.value })} className="w-12 h-10" />
                <input value={themeDraft.bg_soft} onChange={(e) => setThemeDraft({ ...themeDraft, bg_soft: e.target.value })} className="border border-hair px-3 py-2 text-sm flex-1" />
              </div>
            </div>
            <div>
              <p className="eyebrow text-neutral-600 mb-2">Sand background</p>
              <div className="flex gap-3 items-center">
                <input type="color" value={themeDraft.bg_tertiary} onChange={(e) => setThemeDraft({ ...themeDraft, bg_tertiary: e.target.value })} className="w-12 h-10" />
                <input value={themeDraft.bg_tertiary} onChange={(e) => setThemeDraft({ ...themeDraft, bg_tertiary: e.target.value })} className="border border-hair px-3 py-2 text-sm flex-1" />
              </div>
            </div>
            <div>
              <p className="eyebrow text-neutral-600 mb-2">Heading font (Google Fonts name)</p>
              <input value={themeDraft.heading_font} onChange={(e) => setThemeDraft({ ...themeDraft, heading_font: e.target.value })} className="border border-hair px-3 py-2 text-sm w-full" />
            </div>
            <div>
              <p className="eyebrow text-neutral-600 mb-2">Body font</p>
              <input value={themeDraft.body_font} onChange={(e) => setThemeDraft({ ...themeDraft, body_font: e.target.value })} className="border border-hair px-3 py-2 text-sm w-full" />
            </div>
            <button onClick={saveTheme} className="btn-primary mt-6" data-testid="save-theme-btn">Save Theme</button>
          </div>
          <div className="bg-cream border border-hair p-8">
            <p className="eyebrow text-neutral-600 mb-3">Live preview</p>
            <h3 style={{ fontFamily: `'${themeDraft.heading_font}', serif`, color: themeDraft.text }} className="text-4xl mb-4">{siteInfo?.brand_name || "Glitterz SHC"}</h3>
            <p style={{ fontFamily: `'${themeDraft.body_font}', sans-serif`, color: themeDraft.text }} className="text-sm">Quietly extraordinary jewelry.</p>
            <button style={{ background: themeDraft.primary, color: "#fff" }} className="px-6 py-3 mt-6 text-xs uppercase tracking-[0.18em]">Sample button</button>
          </div>
        </section>
      )}

      {editing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center overflow-auto py-10" onClick={() => setEditing(null)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={saveProduct} className="bg-white p-8 max-w-2xl w-full" data-testid="product-modal">
            <h3 className="font-serif text-2xl mb-6">{editing.id ? "Edit" : "New"} Product</h3>
            <div className="grid grid-cols-2 gap-4">
              <input required placeholder="Name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="border border-hair px-3 py-2.5 col-span-2" />
              <textarea required placeholder="Description" rows={3} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="border border-hair px-3 py-2.5 col-span-2" />
              <input required type="number" step="0.01" placeholder="Price (GBP)" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} className="border border-hair px-3 py-2.5" />
              <select value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value, subcategory: "" })} className="border border-hair px-3 py-2.5" data-testid="prod-category">
                {categories.filter((c) => !c.parent_slug).map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
              </select>
              <select value={editing.subcategory || ""} onChange={(e) => setEditing({ ...editing, subcategory: e.target.value })} className="border border-hair px-3 py-2.5" data-testid="prod-subcategory">
                <option value="">— No subcategory —</option>
                {categories.filter((c) => c.parent_slug === editing.category).map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
              </select>
              <input required placeholder="SKU" value={editing.sku} onChange={(e) => setEditing({ ...editing, sku: e.target.value })} className="border border-hair px-3 py-2.5" />
              <input type="number" placeholder="Stock" value={editing.stock} onChange={(e) => setEditing({ ...editing, stock: e.target.value })} className="border border-hair px-3 py-2.5" />
              <div className="col-span-2">
                <p className="eyebrow text-neutral-600 mb-2 text-xs">Audience</p>
                <div className="flex gap-4 flex-wrap" data-testid="prod-audience">
                  {["women", "men", "children"].map((a) => (
                    <label key={a} className="flex items-center gap-2 text-sm capitalize">
                      <input type="checkbox" checked={(editing.audience || []).includes(a)} onChange={(e) => {
                        const cur = new Set(editing.audience || []);
                        if (e.target.checked) cur.add(a); else cur.delete(a);
                        setEditing({ ...editing, audience: Array.from(cur) });
                      }} data-testid={`audience-${a}`} />
                      {a}
                    </label>
                  ))}
                </div>
              </div>
              <input placeholder="Image URLs (comma separated)" value={Array.isArray(editing.images) ? editing.images.join(", ") : editing.images} onChange={(e) => setEditing({ ...editing, images: e.target.value })} className="border border-hair px-3 py-2.5 col-span-2" />
              <input placeholder="Material" value={editing.material || ""} onChange={(e) => setEditing({ ...editing, material: e.target.value })} className="border border-hair px-3 py-2.5 col-span-2" />
              <input placeholder="Collection name (e.g. Heritage, Summer 2026)" value={editing.collection_name || ""} onChange={(e) => setEditing({ ...editing, collection_name: e.target.value })} data-testid="prod-collection" className="border border-hair px-3 py-2.5 col-span-2" />
              <input placeholder="Tags (comma separated)" value={Array.isArray(editing.tags) ? editing.tags.join(", ") : editing.tags} onChange={(e) => setEditing({ ...editing, tags: e.target.value })} className="border border-hair px-3 py-2.5 col-span-2" />
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.is_featured} onChange={(e) => setEditing({ ...editing, is_featured: e.target.checked })} /> Featured</label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.is_active} onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })} /> Active</label>
            </div>
            <div className="flex gap-3 mt-6">
              <button type="submit" className="btn-primary" data-testid="save-product-btn">Save</button>
              <button type="button" onClick={() => setEditing(null)} className="btn-ghost">Cancel</button>
            </div>
          </form>
        </div>
      )}

      {editPost && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center overflow-auto py-10" onClick={() => setEditPost(null)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={savePost} className="bg-white p-8 max-w-3xl w-full" data-testid="post-modal">
            <h3 className="font-serif text-2xl mb-6">{editPost.id ? "Edit" : "New"} Journal Post</h3>
            <div className="grid grid-cols-2 gap-4">
              <input required placeholder="Title" value={editPost.title} onChange={(e) => setEditPost({ ...editPost, title: e.target.value })} className="border border-hair px-3 py-2.5 col-span-2" data-testid="post-title-input" />
              <input required placeholder="Slug (e.g. anatomy-of-a-solitaire)" value={editPost.slug} onChange={(e) => setEditPost({ ...editPost, slug: e.target.value })} className="border border-hair px-3 py-2.5 col-span-2 font-mono text-sm" data-testid="post-slug-input" />
              <input placeholder="Cover image URL" value={editPost.cover_image || ""} onChange={(e) => setEditPost({ ...editPost, cover_image: e.target.value })} className="border border-hair px-3 py-2.5 col-span-2" />
              <textarea placeholder="Excerpt (1-2 sentences)" rows={2} value={editPost.excerpt || ""} onChange={(e) => setEditPost({ ...editPost, excerpt: e.target.value })} className="border border-hair px-3 py-2.5 col-span-2" />
              <textarea required placeholder="Body (Markdown)" rows={12} value={editPost.body} onChange={(e) => setEditPost({ ...editPost, body: e.target.value })} className="border border-hair px-3 py-2.5 col-span-2 font-mono text-sm" data-testid="post-body-input" />
              <input placeholder="Tags (comma separated)" value={Array.isArray(editPost.tags) ? editPost.tags.join(", ") : editPost.tags} onChange={(e) => setEditPost({ ...editPost, tags: e.target.value })} className="border border-hair px-3 py-2.5 col-span-2" />
              <input placeholder="SEO title (optional)" value={editPost.seo_title || ""} onChange={(e) => setEditPost({ ...editPost, seo_title: e.target.value })} className="border border-hair px-3 py-2.5" />
              <input placeholder="SEO description (optional)" value={editPost.seo_description || ""} onChange={(e) => setEditPost({ ...editPost, seo_description: e.target.value })} className="border border-hair px-3 py-2.5" />
              <label className="flex items-center gap-2 text-sm col-span-2">
                <input type="checkbox" checked={editPost.is_published} onChange={(e) => setEditPost({ ...editPost, is_published: e.target.checked })} /> Published
              </label>
            </div>
            <div className="flex gap-3 mt-6">
              <button type="submit" className="btn-primary" data-testid="save-post-btn">Save</button>
              <button type="button" onClick={() => setEditPost(null)} className="btn-ghost">Cancel</button>
            </div>
          </form>
        </div>
      )}

      {editPage && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center overflow-auto py-10" onClick={() => setEditPage(null)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={savePage} className="bg-white p-8 max-w-3xl w-full" data-testid="page-modal">
            <h3 className="font-serif text-2xl mb-6">{editPage.id ? "Edit" : "New"} Page</h3>
            <div className="grid grid-cols-2 gap-4">
              <input required placeholder="Title (e.g. Jewelry Care)" value={editPage.title} onChange={(e) => setEditPage({ ...editPage, title: e.target.value, slug: editPage.slug || e.target.value.toLowerCase().replace(/\s+/g, "-") })} data-testid="page-title-input" className="border border-hair px-3 py-2.5 col-span-2" />
              <input required placeholder="Slug (e.g. jewelry-care)" value={editPage.slug} onChange={(e) => setEditPage({ ...editPage, slug: e.target.value })} data-testid="page-slug-input" className="border border-hair px-3 py-2.5 col-span-2 font-mono text-sm" />
              <textarea placeholder="Excerpt (1 sentence, optional)" rows={2} value={editPage.excerpt || ""} onChange={(e) => setEditPage({ ...editPage, excerpt: e.target.value })} className="border border-hair px-3 py-2.5 col-span-2" />
              <textarea required placeholder="Body (Markdown)" rows={14} value={editPage.body} onChange={(e) => setEditPage({ ...editPage, body: e.target.value })} data-testid="page-body-input" className="border border-hair px-3 py-2.5 col-span-2 font-mono text-sm" />
              <input type="number" placeholder="Order" value={editPage.order} onChange={(e) => setEditPage({ ...editPage, order: parseInt(e.target.value || 0) })} className="border border-hair px-3 py-2.5" />
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={editPage.is_published} onChange={(e) => setEditPage({ ...editPage, is_published: e.target.checked })} /> Published
              </label>
              <input placeholder="SEO title (optional)" value={editPage.seo_title || ""} onChange={(e) => setEditPage({ ...editPage, seo_title: e.target.value })} className="border border-hair px-3 py-2.5 col-span-2" />
              <input placeholder="SEO description (optional)" value={editPage.seo_description || ""} onChange={(e) => setEditPage({ ...editPage, seo_description: e.target.value })} className="border border-hair px-3 py-2.5 col-span-2" />
            </div>
            <div className="flex gap-3 mt-6">
              <button type="submit" className="btn-primary" data-testid="save-page-btn">Save</button>
              <button type="button" onClick={() => setEditPage(null)} className="btn-ghost">Cancel</button>
            </div>
          </form>
        </div>
      )}
    </main>
  );
}

// ---------- OrderRow with inline shipping editor ----------
function OrderRow({ order, onStatus, onSave, onShip }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({
    shipping_carrier: order.shipping_carrier || "Royal Mail Tracked",
    tracking_number: order.tracking_number || "",
    awb_number: order.awb_number || "",
  });
  const [editingAfterShip, setEditingAfterShip] = useState(false);
  useEffect(() => {
    setDraft({
      shipping_carrier: order.shipping_carrier || "Royal Mail Tracked",
      tracking_number: order.tracking_number || "",
      awb_number: order.awb_number || "",
    });
  }, [order.id, order.shipping_carrier, order.tracking_number, order.awb_number]);

  return (
    <div className="border-b border-hair" data-testid={`admin-order-${order.id}`}>
      <div className="grid grid-cols-[1fr_1.4fr_1fr_1fr_1fr_60px] gap-3 p-4 items-center text-sm">
        <span className="font-mono text-xs">{order.id.slice(0, 8)}</span>
        <span className="truncate">{order.customer_email}</span>
        <span>{formatGBP(order.total)}</span>
        <span className="capitalize">{order.payment_status}</span>
        <span className="capitalize">{order.status}</span>
        <button onClick={() => setOpen(!open)} className="text-xs underline justify-self-end" data-testid={`order-toggle-${order.id}`}>
          {open ? "Close" : "Manage"}
        </button>
      </div>
      {open && (
        <div className="px-4 pb-4 pt-1 bg-cream/40 grid md:grid-cols-2 gap-6">
          <div className="space-y-3">
            <p className="eyebrow text-neutral-600 text-[10px]">Status</p>
            <select onChange={(e) => onStatus(e.target.value)} value={order.status}
              className="border border-hair text-sm px-2 py-2 bg-white w-full">
              {["awaiting_payment","processing","shipped","delivered","cancelled"].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <p className="eyebrow text-neutral-600 text-[10px] mt-4">Items</p>
            <ul className="text-xs text-neutral-700 space-y-1">
              {(order.items || []).map((it, i) => (
                <li key={i}>{it.name} × {it.quantity} — {formatGBP((it.price || 0) * (it.quantity || 1))}</li>
              ))}
            </ul>
            {order.shipping_address && (
              <>
                <p className="eyebrow text-neutral-600 text-[10px] mt-4">Ship to</p>
                <p className="text-xs text-neutral-700 whitespace-pre-line">
                  {[order.shipping_address.name, order.shipping_address.line1,
                    order.shipping_address.line2, order.shipping_address.city,
                    order.shipping_address.postal_code, order.shipping_address.country
                  ].filter(Boolean).join("\n")}
                </p>
              </>
            )}
          </div>
          <div className="space-y-3">
            <p className="eyebrow text-neutral-600 text-[10px]">Shipping & tracking</p>
            <label className="block">
              <span className="text-[11px] uppercase tracking-wide text-neutral-600">Carrier</span>
              <select value={draft.shipping_carrier} onChange={(e) => setDraft({ ...draft, shipping_carrier: e.target.value })}
                data-testid={`order-carrier-${order.id}`}
                className="border border-hair text-sm px-2 py-2 bg-white w-full mt-1">
                {["Royal Mail Tracked", "Royal Mail Special Delivery", "DHL", "Parcelforce", "DPD", "Evri", "Other"].map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-[11px] uppercase tracking-wide text-neutral-600">Tracking number</span>
              <input value={draft.tracking_number} onChange={(e) => setDraft({ ...draft, tracking_number: e.target.value })}
                data-testid={`order-tracking-${order.id}`}
                placeholder="e.g. AB123456789GB"
                className="border border-hair text-sm px-2 py-2 w-full mt-1 font-mono" />
            </label>
            <label className="block">
              <span className="text-[11px] uppercase tracking-wide text-neutral-600">Air Waybill (AWB) — optional</span>
              <input value={draft.awb_number} onChange={(e) => setDraft({ ...draft, awb_number: e.target.value })}
                data-testid={`order-awb-${order.id}`}
                placeholder="e.g. 235-12345678"
                className="border border-hair text-sm px-2 py-2 w-full mt-1 font-mono" />
            </label>
            <div className="flex gap-2 pt-2 flex-wrap">
              <button onClick={() => onSave(draft)} className="btn-ghost text-xs" data-testid={`order-save-${order.id}`}>Save details</button>
              {order.payment_status === "paid" && order.status !== "shipped" && order.status !== "delivered" && (
                <button onClick={() => onShip(draft)} className="btn-primary text-xs flex items-center gap-1" data-testid={`order-ship-${order.id}`}>
                  <Truck className="w-3.5 h-3.5" strokeWidth={1.25} /> Ship & notify customer
                </button>
              )}
              {(order.status === "shipped" || order.status === "delivered") && !editingAfterShip && (
                <button onClick={() => setEditingAfterShip(true)} className="text-xs underline">Edit after ship</button>
              )}
            </div>
            {order.tracking_url && (
              <p className="text-xs text-neutral-600">Public link: <a href={order.tracking_url} target="_blank" rel="noreferrer" className="underline break-all">{order.tracking_url}</a></p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- Marketing tab ----------
function MarketingTab() {
  const [audience, setAudience] = useState({ subscribers: 0, customers: 0 });
  const [campaigns, setCampaigns] = useState([]);
  const [form, setForm] = useState({
    subject: "",
    body_html: "<p>Hi,</p>\n<p>We just added new pieces to the collection — take a look.</p>\n<p><a href='https://glitterzshc.com/shop'>Shop now</a></p>",
    recipients: "subscribers",
    promo_code: "",
    test_email: "",
  });
  const [sending, setSending] = useState(false);

  const refresh = () => {
    api.get("/admin/marketing/audience").then(({ data }) => setAudience(data)).catch(() => {});
    api.get("/admin/marketing/campaigns").then(({ data }) => setCampaigns(data)).catch(() => {});
  };
  useEffect(() => { refresh(); }, []);

  const send = async (kind) => {
    if (!form.subject || !form.body_html) { toast.error("Subject and body required"); return; }
    const payload = { ...form, recipients: kind === "test" ? "test" : form.recipients };
    if (kind === "test" && !payload.test_email) { toast.error("Enter a test email"); return; }
    setSending(true);
    try {
      const { data } = await api.post("/admin/marketing/send", payload);
      toast.success(`Sent ${data.sent}/${data.audience_size} (${data.failed} failed)`);
      refresh();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSending(false); }
  };

  return (
    <section data-testid="admin-marketing" className="max-w-4xl space-y-8">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <div className="border border-hair p-4">
          <p className="eyebrow text-neutral-600 text-[10px]">Newsletter subscribers</p>
          <p className="font-serif text-3xl mt-1" data-testid="marketing-subs-count">{audience.subscribers}</p>
        </div>
        <div className="border border-hair p-4">
          <p className="eyebrow text-neutral-600 text-[10px]">Registered customers</p>
          <p className="font-serif text-3xl mt-1" data-testid="marketing-cust-count">{audience.customers}</p>
        </div>
      </div>

      <div className="border border-hair p-6 space-y-4">
        <p className="eyebrow text-neutral-600">Compose campaign</p>
        <label className="block">
          <span className="text-xs uppercase tracking-wide text-neutral-600">Subject</span>
          <input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })}
            placeholder="New arrivals — 15% off this weekend" data-testid="marketing-subject"
            className="border border-hair px-3 py-2.5 text-sm w-full mt-1" />
        </label>
        <label className="block">
          <span className="text-xs uppercase tracking-wide text-neutral-600">Body (HTML)</span>
          <textarea rows={10} value={form.body_html} onChange={(e) => setForm({ ...form, body_html: e.target.value })}
            data-testid="marketing-body"
            className="border border-hair px-3 py-2.5 text-sm w-full mt-1 font-mono" />
          <p className="text-[11px] text-neutral-500 mt-1">Use basic HTML — &lt;p&gt;, &lt;a href&gt;, &lt;strong&gt;, &lt;img src&gt; all work.</p>
        </label>
        <div className="grid md:grid-cols-2 gap-4">
          <label className="block">
            <span className="text-xs uppercase tracking-wide text-neutral-600">Send to</span>
            <select value={form.recipients} onChange={(e) => setForm({ ...form, recipients: e.target.value })}
              data-testid="marketing-recipients"
              className="border border-hair px-3 py-2.5 text-sm w-full mt-1 bg-white">
              <option value="subscribers">Newsletter subscribers ({audience.subscribers})</option>
              <option value="customers">Registered customers ({audience.customers})</option>
              <option value="both">Both audiences (deduped)</option>
            </select>
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-wide text-neutral-600">Promo code (optional)</span>
            <input value={form.promo_code} onChange={(e) => setForm({ ...form, promo_code: e.target.value })}
              placeholder="LOVE15" data-testid="marketing-promo"
              className="border border-hair px-3 py-2.5 text-sm w-full mt-1 uppercase font-mono" />
            <p className="text-[11px] text-neutral-500 mt-1">If provided, a styled code box is rendered in the email. Make sure the code exists in <b>Promos</b>.</p>
          </label>
        </div>
        <div className="border-t border-hair pt-4 flex flex-wrap gap-3 items-end">
          <label className="block flex-1 min-w-[200px]">
            <span className="text-xs uppercase tracking-wide text-neutral-600">Test send to</span>
            <input value={form.test_email} onChange={(e) => setForm({ ...form, test_email: e.target.value })}
              placeholder="your-email@example.com" data-testid="marketing-test-email"
              className="border border-hair px-3 py-2.5 text-sm w-full mt-1" />
          </label>
          <button onClick={() => send("test")} disabled={sending}
            data-testid="marketing-test-btn"
            className="btn-ghost text-xs">Send test only</button>
          <button onClick={() => {
            const audsize = form.recipients === "subscribers" ? audience.subscribers
              : form.recipients === "customers" ? audience.customers
              : audience.subscribers + audience.customers;
            if (!window.confirm(`Send campaign to ~${audsize} recipients? This takes ~${Math.ceil(audsize * 1.2 / 60)} min and cannot be undone.`)) return;
            send("all");
          }} disabled={sending}
            data-testid="marketing-send-btn"
            className="btn-primary text-xs">{sending ? "Sending…" : "Send to audience"}</button>
        </div>
      </div>

      <div>
        <p className="eyebrow text-neutral-600 mb-3">Recent campaigns</p>
        {campaigns.length === 0 ? <p className="text-sm text-neutral-500">No campaigns yet.</p> : (
          <div className="border border-hair">
            <div className="grid grid-cols-[2fr_1fr_1fr_1fr_1fr] gap-3 p-3 bg-cream text-xs uppercase tracking-[0.18em] text-neutral-600 border-b border-hair">
              <span>Subject</span><span>Audience</span><span>Sent</span><span>Failed</span><span>Date</span>
            </div>
            {campaigns.map((c) => (
              <div key={c.id} className="grid grid-cols-[2fr_1fr_1fr_1fr_1fr] gap-3 p-3 text-sm border-b border-hair">
                <span className="truncate">{c.subject}</span>
                <span>{c.recipients}</span>
                <span>{c.sent}/{c.audience_size}</span>
                <span className={c.failed ? "text-red-700" : ""}>{c.failed}</span>
                <span className="text-xs text-neutral-500">{new Date(c.created_at).toLocaleString("en-GB")}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
